import type { Allowances, InvitationStatus, OrgRole, RequestStatus } from "../types";

/** Invitation and request states, and the seat check (FR-ADMIN-026, 027, 028). Pure, so the rules are testable. */

export interface StoredDecision {
  status: "pending" | "accepted" | "declined" | "revoked";
  expiresAt: number;
}

/** A pending invitation past its 14 days reads as expired; decided ones keep their decision. */
export function invitationStatus(invitation: StoredDecision, now: number): InvitationStatus {
  if (invitation.status === "pending" && now >= invitation.expiresAt) return "expired";
  return invitation.status;
}

export function isOpen(invitation: StoredDecision, now: number): boolean {
  return invitationStatus(invitation, now) === "pending";
}

/** Project access requests expire after 14 days without a decision (FR-ADMIN-028). */
export function requestStatus(
  request: { status: "pending" | "approved" | "declined"; expiresAt: number },
  now: number,
): RequestStatus {
  if (request.status === "pending" && now >= request.expiresAt) return "expired";
  return request.status;
}

export interface SeatUsage {
  seats: number;
  guests: number;
}

/** Members and pending invitations count against seats; guests against the guest allowance (FR-ADMIN-027). */
export function seatUsage(
  memberRoles: readonly OrgRole[],
  pendingRoles: readonly OrgRole[],
): SeatUsage {
  const all = [...memberRoles, ...pendingRoles];
  const guests = all.filter((role) => role === "guest").length;
  return { seats: all.length - guests, guests };
}

export type SeatCheck = { ok: true } | { ok: false; message: string };

function plural(count: number, one: string, many: string): string {
  return `${String(count)} ${count === 1 ? one : many}`;
}

function limitMessage(kind: "seat" | "guest", allowed: number, used: number): string {
  const label = kind === "seat" ? ["seat", "seats"] : ["guest place", "guest places"];
  const [one, many] = label as [string, string];
  const free = Math.max(0, allowed - used);
  if (free === 0) {
    // The PRD's exact wording (FR-ADMIN-027). No purchase path or price exists (D-004).
    return `All ${plural(allowed, one, many)} are in use — remove someone or ask Orthos to add ${many}`;
  }
  return `Only ${String(free)} of ${plural(allowed, one, many)} ${free === 1 ? "is" : "are"} free — invite fewer people, remove someone or ask Orthos to add ${many}`;
}

/** Inviting is blocked when the new invitations would pass an allowance (FR-ADMIN-027, 056). */
export function checkSeats(
  allowances: Allowances,
  usage: SeatUsage,
  newRoles: readonly OrgRole[],
): SeatCheck {
  const newGuests = newRoles.filter((role) => role === "guest").length;
  const newSeats = newRoles.length - newGuests;
  if (newSeats > 0 && usage.seats + newSeats > allowances.seats) {
    return { ok: false, message: limitMessage("seat", allowances.seats, usage.seats) };
  }
  if (newGuests > 0 && usage.guests + newGuests > allowances.guests) {
    return { ok: false, message: limitMessage("guest", allowances.guests, usage.guests) };
  }
  return { ok: true };
}

/** Organisation setup is the Owner's first-run step until it is done (FR-ADMIN-009). */
export function needsOrganisationSetup(role: OrgRole | undefined, setupComplete: boolean): boolean {
  return role === "owner" && !setupComplete;
}
